import React, { useCallback, useEffect, useState } from 'react'
import {
  CheckOutlined,
  LockOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Card,
  Flex,
  Input,
  Popconfirm,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useUnit } from 'effector-react'
import { useUrlFilters } from '../../../libs/useUrlFilters'
import {
  $alerts,
  $alertsHasError,
  $alertsTotal,
  $blockedAlertsCount,
  $pendingAlertId,
  $pendingAlertsCount,
  alertBlockRequested,
  alertFalsePositiveRequested,
  alertsCountsRequested,
  alertsRequested,
  getAlertsFx,
} from './model'
import {
  LEVEL_COLORS,
  LEVEL_LABELS,
  LEVEL_OPTIONS,
  PROCESSING_STATUS_COLORS,
  PROCESSING_STATUS_LABELS,
  SOURCE_LABELS,
  SOURCE_OPTIONS,
  WALLET_TYPE_LABELS,
} from './constants'
import { WalletAlert, WalletAlertsWhere } from './types'

const FILTER_CONFIG = {
  tab: { type: 'string' as const },
  source: { type: 'array' as const },
  level: { type: 'array' as const },
  walletId: { type: 'string' as const },
  page: { type: 'string' as const, pagination: true },
  pageSize: { type: 'string' as const, pagination: true },
}

const DEFAULT_PAGE_SIZE = 20

const formatDate = (value: string) => new Date(value).toLocaleString('ru-RU')

const filterOption = (inputValue: string, option?: { label?: string }) => {
  if (!option?.label) return false
  return option.label.toLowerCase().includes(inputValue.trim().toLowerCase())
}

const columns: ColumnsType<WalletAlert> = [
  {
    title: 'Объект тревоги',
    dataIndex: 'source',
    key: 'source',
    render: (source: WalletAlert['source']) => (
      <Tag>{SOURCE_LABELS[source]}</Tag>
    ),
  },
  {
    title: 'ID кошелька',
    dataIndex: ['wallet', 'id'],
    key: 'walletId',
  },
  {
    title: 'Кошелек',
    dataIndex: 'wallet',
    key: 'wallet',
    render: (wallet: WalletAlert['wallet']) => (
      <Flex vertical gap={2}>
        <Typography.Text strong>{wallet.name}</Typography.Text>
        <Typography.Text
          type="secondary"
          copyable={{ text: wallet.address }}
          ellipsis={{ tooltip: wallet.address }}
          style={{ maxWidth: 220 }}
        >
          {wallet.address}
        </Typography.Text>
        <Typography.Text type="secondary">
          {WALLET_TYPE_LABELS[wallet.type]} · {wallet.currency}
        </Typography.Text>
      </Flex>
    ),
  },
  {
    title: 'Транзакция',
    key: 'transaction',
    render: (alert: WalletAlert) => {
      const { transaction } = alert

      if (!transaction) {
        return '—'
      }

      return (
        <Flex vertical gap={2}>
          <Typography.Text strong>
            {transaction.amount} {alert.wallet.currency}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            copyable={{ text: transaction.txHash }}
            ellipsis={{ tooltip: transaction.txHash }}
            style={{ maxWidth: 220 }}
          >
            Хеш: {transaction.txHash}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            copyable={{ text: transaction.senderAddress }}
            ellipsis={{ tooltip: transaction.senderAddress }}
            style={{ maxWidth: 220 }}
          >
            От: {transaction.senderAddress}
          </Typography.Text>
          <Typography.Text type="secondary">
            {formatDate(transaction.occurredAt)}
          </Typography.Text>
        </Flex>
      )
    },
  },
  {
    title: 'AML-скор',
    dataIndex: 'amlScore',
    key: 'amlScore',
    render: (amlScore: string) => `${Number(amlScore)}%`,
  },
  {
    title: 'Флаги риска',
    dataIndex: 'riskFlags',
    key: 'riskFlags',
    render: (riskFlags: WalletAlert['riskFlags']) => {
      if (riskFlags.length === 0) {
        return '—'
      }

      return (
        <Flex wrap="wrap" gap={4}>
          {riskFlags.map((flag, index) => (
            <Tag key={index} style={{ margin: 0 }}>
              {flag.label}
            </Tag>
          ))}
        </Flex>
      )
    },
  },
  {
    title: 'Уровень тревоги',
    dataIndex: 'level',
    key: 'level',
    render: (level: WalletAlert['level']) => (
      <Tag color={LEVEL_COLORS[level]}>{LEVEL_LABELS[level]}</Tag>
    ),
  },
  {
    title: 'Статус обработки',
    dataIndex: 'processingStatus',
    key: 'processingStatus',
    render: (status: WalletAlert['processingStatus']) => (
      <Tag color={PROCESSING_STATUS_COLORS[status]}>
        {PROCESSING_STATUS_LABELS[status]}
      </Tag>
    ),
  },
]

export const WalletGuardAlertsPage = () => {
  const { filters, setFilters } = useUrlFilters(FILTER_CONFIG)

  const {
    alerts,
    total,
    loading,
    hasError,
    pendingCount,
    blockedCount,
    requestAlerts,
    requestCounts,
    pendingAlertId,
    blockAlert,
    markFalsePositive,
  } = useUnit({
    alerts: $alerts,
    total: $alertsTotal,
    loading: getAlertsFx.pending,
    hasError: $alertsHasError,
    pendingCount: $pendingAlertsCount,
    blockedCount: $blockedAlertsCount,
    requestAlerts: alertsRequested,
    requestCounts: alertsCountsRequested,
    pendingAlertId: $pendingAlertId,
    blockAlert: alertBlockRequested,
    markFalsePositive: alertFalsePositiveRequested,
  })

  const tab = filters.tab === 'blocked' ? 'blocked' : 'pending'
  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || DEFAULT_PAGE_SIZE
  const hasFilters = Boolean(
    filters.source?.length || filters.level?.length || filters.walletId,
  )

  const loadAlerts = useCallback(() => {
    const where: WalletAlertsWhere = {
      processingStatus: [tab],
      source: filters.source as WalletAlertsWhere['source'],
      level: filters.level as WalletAlertsWhere['level'],
    }

    if (filters.walletId) {
      where.walletId = [Number(filters.walletId)]
    }

    requestAlerts({ page, limit: pageSize, where })
  }, [
    requestAlerts,
    tab,
    page,
    pageSize,
    filters.source,
    filters.level,
    filters.walletId,
  ])

  useEffect(() => {
    loadAlerts()
  }, [loadAlerts])

  // The input keeps a draft value; the filter is applied on Enter or blur.
  const [walletIdInput, setWalletIdInput] = useState(filters.walletId ?? '')

  useEffect(() => {
    setWalletIdInput(filters.walletId ?? '')
  }, [filters.walletId])

  const applyWalletId = () => {
    setFilters({ walletId: walletIdInput, page: undefined })
  }

  useEffect(() => {
    requestCounts()
  }, [requestCounts])

  const refresh = () => {
    loadAlerts()
    requestCounts()
  }

  const actionsColumn: ColumnsType<WalletAlert>[number] = {
    title: 'Действия',
    key: 'actions',
    fixed: 'right',
    render: (alert: WalletAlert) => {
      const isPending = pendingAlertId === alert.id
      const isDisabled = pendingAlertId !== null

      return (
        <Flex vertical gap={4}>
          <Popconfirm
            title="Отметить тревогу как ложную?"
            description="Транзакция будет исключена из расчёта рисков"
            okText="Подтвердить"
            cancelText="Отмена"
            disabled={isDisabled}
            onConfirm={() => markFalsePositive(alert.id)}
          >
            <Button
              size="small"
              icon={<CheckOutlined />}
              loading={isPending}
              disabled={isDisabled}
            >
              Ложная тревога
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Заблокировать кошелёк?"
            description={alert.wallet.address}
            okText="Заблокировать"
            okButtonProps={{ danger: true }}
            cancelText="Отмена"
            disabled={isDisabled}
            onConfirm={() => blockAlert(alert.id)}
          >
            <Button
              size="small"
              danger
              icon={<LockOutlined />}
              loading={isPending}
              disabled={isDisabled}
            >
              Заблокировать
            </Button>
          </Popconfirm>
        </Flex>
      )
    },
  }

  // Actions are available only for alerts that have not been processed yet.
  const tableColumns = tab === 'pending' ? [...columns, actionsColumn] : columns

  return (
    <Flex vertical gap={24}>
      <Flex justify="space-between" align="flex-start" gap={16}>
        <Flex vertical gap={4}>
          <Typography.Title level={1} style={{ margin: 0 }}>
            Тревоги
          </Typography.Title>
          <Typography.Text type="secondary">
            Подозрительные транзакции, требующие проверки
          </Typography.Text>
        </Flex>
        <Button icon={<ReloadOutlined />} onClick={refresh}>
          Обновить
        </Button>
      </Flex>

      <Card
        activeTabKey={tab}
        onTabChange={(key) => setFilters({ tab: key, page: undefined })}
        tabList={[
          {
            key: 'pending',
            label: (
              <>
                <WarningOutlined /> На проверке {pendingCount}
              </>
            ),
          },
          {
            key: 'blocked',
            label: (
              <>
                <LockOutlined /> Заблокированные {blockedCount}
              </>
            ),
          },
        ]}
      >
        <Flex gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Объект тревоги"
            filterOption={filterOption}
            options={SOURCE_OPTIONS}
            value={filters.source}
            onChange={(value) => setFilters({ source: value, page: undefined })}
            style={{ minWidth: 220 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Уровень тревоги"
            filterOption={filterOption}
            options={LEVEL_OPTIONS}
            value={filters.level}
            onChange={(value) => setFilters({ level: value, page: undefined })}
            style={{ minWidth: 220 }}
          />
          <Input
            allowClear
            placeholder="ID кошелька"
            value={walletIdInput}
            onChange={(event) =>
              setWalletIdInput(event.target.value.replace(/\D/g, ''))
            }
            onPressEnter={applyWalletId}
            onBlur={applyWalletId}
            onClear={() => setFilters({ walletId: undefined, page: undefined })}
            style={{ width: 160 }}
          />
          {hasFilters && (
            <Button
              onClick={() =>
                setFilters({
                  source: undefined,
                  level: undefined,
                  walletId: undefined,
                  page: undefined,
                })
              }
            >
              Сбросить
            </Button>
          )}
        </Flex>

        {hasError ? (
          <Alert
            type="error"
            showIcon
            message="Не удалось загрузить тревоги"
            action={<Button onClick={loadAlerts}>Повторить</Button>}
          />
        ) : (
          <Table
            dataSource={alerts}
            columns={tableColumns}
            rowKey="id"
            loading={loading}
            scroll={{ x: true }}
            locale={{
              emptyText:
                tab === 'pending'
                  ? 'Нет тревог на проверке'
                  : 'Нет заблокированных тревог',
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
            }}
            onChange={(pagination) => {
              setFilters({
                page: String(pagination.current),
                pageSize: String(pagination.pageSize),
              })
            }}
          />
        )}
      </Card>
    </Flex>
  )
}
